
import VueFilters from "@/vue-filters";
import { Component, Prop, Vue } from "vue-property-decorator";
import {
  CashOrderStatusEnum,
  ICashOrderDTO,
  IField,
  IRequestedAmount,
  IRequestHistorialsService,
  ISolCustomerCurrency,
  resolveSolCustomerOrderDescriptionKey,
  resolveSolCustomerOrderType,
} from "@/entities";
import { Getter } from "vuex-class";

@Component({
  name: "approvals-table",
  mixins: [VueFilters],
})
export default class ApprovalsTable extends Vue {
  @Prop({ required: true }) title!: string;
  @Prop({ required: true }) orders!: ICashOrderDTO[];
  @Prop({ required: true }) statusColor!: string;
  @Prop({ required: false, default: false }) showServiceCode!: boolean;
  @Prop({ required: false, default: true }) collapsed!: boolean;

  @Getter("getSolCustomerCurrencies") currencies!: ISolCustomerCurrency[];

  collapsedLocal = true;

  mounted(): void {
    this.collapsedLocal = this.collapsed;
  }

  get visibleFields(): IField[] {
    return this.fields.filter((field) => field.visible !== false);
  }

  get fields(): IField[] {
    return [
      {
        key: "actions-left",
        label: "",
        sortable: false,
        thClass: "table-header pl-4",
        tdClass: "table-cell-first align-middle position-relative",
      },
      {
        key: "orderType",
        label: this.$t("column.orderType") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "serviceCode",
        label: this.$t("column.id") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
        visible: this.showServiceCode,
      },
      {
        key: "createdAt",
        label: this.$t("column.creation_date") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "updatedAt",
        label: this.$t("column.update_date") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "username",
        label: this.$t("label.iduser") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "status",
        label: this.$t("column.status") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "requestedAmounts",
        label: this.$t("column.amount") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
        formatter: (_value, _key, item) => {
          const requestedAmounts = this.toRequestHistorialsService(item.message).requestedAmounts;
          if (!requestedAmounts?.length) {
            return [];
          }

          return this.sumRequestedAmounts(requestedAmounts);
        },
      },
    ];
  }

  resolveOrderType(productCode?: string): string {
    const orderType = resolveSolCustomerOrderType(productCode);
    const descriptionKey = resolveSolCustomerOrderDescriptionKey(orderType);

    if (!descriptionKey) {
      return productCode || "";
    }

    return this.$t(descriptionKey).toString();
  }

  statusLabel(status: CashOrderStatusEnum): string {
    switch (status) {
      case CashOrderStatusEnum.PENDING_APPROVAL:
        return "label.pending_approval";
      case CashOrderStatusEnum.APPROVED_APPROVAL:
        return "label.approved_approval";
      case CashOrderStatusEnum.REJECTED_APPROVAL:
        return "label.rejected_approval";
      default:
        return String(status);
    }
  }

  private resolveCurrencyIsoCode(requestedAmount: IRequestedAmount): string {
    const currencyId = requestedAmount.currencyId;
    if (!currencyId) {
      return "";
    }

    const currency = this.currencies?.find((c) => String(c.currencyId) === String(currencyId));
    return currency?.isoCode || "";
  }

  sumRequestedAmounts(requestedAmounts: IRequestedAmount[] | undefined): IRequestedAmount[] {
    const amountsByCurrency: IRequestedAmount[] = [];
    requestedAmounts?.forEach((value) => {
      const amountByCurrency = amountsByCurrency.find(
        (requestedAmount) => requestedAmount.currencyId === value.currencyId
      );

      if (amountByCurrency) {
        amountByCurrency.totalAmount =
          (amountByCurrency.totalAmount || 0) + (value.totalAmount || 0);
      } else {
        amountsByCurrency.push({ ...value });
      }
    });
    return amountsByCurrency;
  }

  toRequestHistorialsService(message: string): IRequestHistorialsService {
    return JSON.parse(message) as IRequestHistorialsService;
  }
}
